import { ICompany } from '../common/models/Company'
import { IconType } from '../components/icon/IconType'
import {
  CompanySetupTaskId,
  COMPANY_SETUP_TASK_IDS,
  companySetupTaskDone,
} from '../common/helpers/companySetup'

// One row in the support-only "Complete company details" checklist. `done` comes
// from the shared predicate; this util only owns presentation — the icon, the
// settings screen to route to (AI tasks carry a `?wizard=` param the target page
// reads to auto-open its generator), and the i18n keys.
export interface ICompanySetupTask {
  id: CompanySetupTaskId
  labelKey: string
  descriptionKey: string
  icon: IconType
  path: string
  done: boolean
}

// The AI-generation steps build on each other, so the guided checklist flow walks
// them in this order — each stays locked until every earlier one is done. (Doing
// them out of order directly in settings stays allowed; only the modal enforces it.)
export const AI_SETUP_TASK_ORDER: CompanySetupTaskId[] = [
  'tone',
  'mission',
  'dna',
  'framework',
  'skills',
]

type TaskPresentation = Omit<ICompanySetupTask, 'id' | 'done'>

const PRESENTATION: Record<CompanySetupTaskId, TaskPresentation> = {
  logo: {
    labelKey: 'brandingSettings.logoLabel',
    descriptionKey: 'completeCompany.tasks.logo.description',
    icon: 'Image',
    path: '/company/settings/branding',
  },
  header: {
    labelKey: 'brandingSettings.headerLabel',
    descriptionKey: 'completeCompany.tasks.header.description',
    icon: 'Image',
    path: '/company/settings/branding',
  },
  branding: {
    labelKey: 'brandingSettings.colorTextTitle',
    descriptionKey: 'completeCompany.tasks.branding.description',
    icon: 'Pencil',
    path: '/company/settings/branding',
  },
  tone: {
    labelKey: 'aiContext.toneOfVoice.label',
    descriptionKey: 'completeCompany.tasks.tone.description',
    icon: 'Ai',
    path: '/company/settings/ai-context?wizard=tone',
  },
  mission: {
    labelKey: 'aiContext.mission.label',
    descriptionKey: 'completeCompany.tasks.mission.description',
    icon: 'Ai',
    path: '/company/settings/ai-context?wizard=mission',
  },
  dna: {
    labelKey: 'general.feedback.culture',
    descriptionKey: 'completeCompany.tasks.dna.description',
    icon: 'Culture',
    path: '/company/settings/culture?wizard=dna',
  },
  framework: {
    labelKey: 'general.job_other',
    descriptionKey: 'completeCompany.tasks.framework.description',
    icon: 'JobFamily',
    path: '/company/settings/jobs?wizard=framework',
  },
  skills: {
    labelKey: 'general.skills',
    descriptionKey: 'completeCompany.tasks.skills.description',
    icon: 'Skills',
    path: '/company/settings/skills?wizard=skills',
  },
}

export const getCompanySetupTasks = (
  company: ICompany
): ICompanySetupTask[] => {
  const done = companySetupTaskDone(company)
  return COMPANY_SETUP_TASK_IDS.map((id) => ({
    id,
    ...PRESENTATION[id],
    done: done[id],
  }))
}
